<script setup lang="ts">
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import type { InfoLevel } from "@/model/baseTypes";
import { infoLevelInfo } from "@/model/baseTypes";
import type { Icon } from "@/model/icon";

// Design-system mapping — see baseTypes.ts. Callers pass our in-app level
// names (default/ok/warn/error); the spec labels them description/info/alert/
// warning. The optional `icon` prop lets a caller override the level's icon
// (used for ALM-branded callouts, e.g. a Jira logo on an "alert").
const props = withDefaults(
  defineProps<{ level?: InfoLevel; size?: "medium" | "small"; icon?: Icon }>(),
  { level: "ok", size: "medium", icon: undefined },
);

const iconName = () => props.icon ?? infoLevelInfo[props.level].icon;
</script>

<template>
  <div
    class="info-message"
    :class="size"
    :style="{ backgroundColor: infoLevelInfo[level].backColor }"
  >
    <SvgIcon :style="{ color: infoLevelInfo[level].color }" :name="iconName()" class="icon" />
    <div class="body">
      <slot />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/mixins/typography";
@use "@/styles/variables/colors";

.info-message {
  display: flex;
  align-items: flex-start;
  color: colors.$text;
  border-radius: 6px;

  &.medium {
    @include typography.medium("semi-bold");

    padding: 16px;
    gap: 12px;

    .icon {
      width: 20px;
      height: 20px;
    }
  }

  &.small {
    @include typography.small("semi-bold");

    padding: 12px;
    gap: 8px;

    .icon {
      width: 16px;
      height: 16px;
    }

    .body {
      margin-top: -1px; // Make text appear aligned with the icon
    }
  }

  .icon {
    flex-shrink: 0;
  }

  .body {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
  }
}
</style>
